/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// ── Persisted chat-agent provider preference (single identifier) ──────────────
//
// ONE durable choice of which chat-agent provider backs the agent: `claw` (the
// built-in default) or `bedrock`. A single identifier can't hold a
// contradictory state. Toggled from the debug controls radio group and
// persisted across reloads; distinct from the session `?agent=<id>` URL flag,
// which is a one-off override (see `agentProviderFlag`).
//
// The observable (`agentProvider$`) is exposed DIRECTLY as a `MutableValue` so
// any consumer reacts to changes AND drives them (the settings radio binds it as
// its controller; the chat header pill subscribes). `createPersistentItem`
// backs the durable store; persistence is wired as an
// `onData` subscription on the observable, so any write — from the radio or a
// direct `.value =` — is saved. `selectedAgentProviderId()` reads `.value`
// SYNCHRONOUSLY on the boot / per-turn hot path, and `app.ts` awaits
// `hydrateAgentProviderPref()` before its first selection read. A corrupt/legacy
// stored value fails the validator and falls back to the default (`claw`).

import { data, type MutableValue } from '@amazon/vinyl-observable'
import { createPersistentItem, globalRef } from '@amazon/vinyl-util'
import { isOneOf } from '@amazon/vinyl-validation'
import {
    type AgentProviderId,
    BEDROCK_PROVIDER_ID,
    CLAW_PROVIDER_ID,
} from '@/blackbolt/agent/agentProviderIds'
import { ctx } from '@/util/context'
import { globalData } from '@/util/globalData'

const { errorHandler } = ctx('agentProviderPref')

const persisted = createPersistentItem<AgentProviderId>({
    key: 'agentProvider',
    defaultValue: CLAW_PROVIDER_ID,
    // A stored value outside the closed set (legacy junk, manual tampering) is
    // rejected by the validator and falls back to the default.
    validator: isOneOf(CLAW_PROVIDER_ID, BEDROCK_PROVIDER_ID).assert,
})

/**
 * The selected chat-agent provider id. Exposed directly so consumers subscribe
 * (chat header pill) and drive it (settings radio controller). Reading `.value`
 * is sync-safe on the boot / per-turn hot path.
 */
export const agentProvider$: MutableValue<AgentProviderId> = globalData(() => {
    const provider$ = data<AgentProviderId>(CLAW_PROVIDER_ID)
    // Persist every change. `hydrated` gates the initial hydration write: `onData`
    // fires immediately with the current value and again when hydration sets the
    // stored value, neither of which should be written back. After hydration, a
    // real user change (radio / `.value =`) persists.
    provider$.onData((id) => {
        if (!hydrationRef.value.hydrated) return
        persisted.set(id).catch(errorHandler)
    })
    return provider$
})

const hydrationRef = globalRef(() => ({ hydrated: false }))

/**
 * Load the persisted preference into the sync-readable observable. Awaited early
 * in boot (before `selectedAgentProviderId()` is first read) so the choice takes
 * effect on the load after it was changed, without a URL flag.
 */
export async function hydrateAgentProviderPref(): Promise<void> {
    const stored = await persisted.get()
    agentProvider$.value = stored
    hydrationRef.value.hydrated = true
}

/** Sync read of the selected provider id; safe on the boot / per-turn hot path. */
export function preferredAgentProvider(): AgentProviderId {
    return agentProvider$.value
}
